import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Ban, Gift, Loader2, Pencil, Send, UserPlus, X } from 'lucide-react'
import { MarketingShell } from './MarketingShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { describeApiError } from '@/lib/apiError'
import { formatPKRCompact } from '@/lib/utils'
import type { CustomerRow, GiftCard } from '@/types'

type StatusFilter = 'active' | 'spent' | 'expired' | 'void' | 'all'

const FILTERS: { id: StatusFilter; label: string }[] = [
  { id: 'active', label: 'Live' },
  { id: 'spent', label: 'Used up' },
  { id: 'expired', label: 'Expired' },
  { id: 'void', label: 'Cancelled' },
  { id: 'all', label: 'All' },
]

/** Exact rupees, with separators. Individual cards are reconciled one by one
 *  against real vouchers, so these stay exact; only the headline goes
 *  compact (formatPKRCompact), with the exact figure on hover. */
function pkr(n: number): string {
  return 'Rs ' + n.toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

/** What a card is, in one word, using the server's own verdict rather than
 *  re-deriving it here — the two used to disagree on screen. */
function cardState(g: GiftCard): { label: string; className: string } {
  if (g.status === 'void') return { label: 'Cancelled', className: 'text-muted-foreground' }
  if (g.balance <= 0) return { label: 'Used up', className: 'text-muted-foreground' }
  if (g.expired) return { label: 'Expired', className: 'text-amber-600' }
  return { label: 'Live', className: 'text-emerald-600' }
}

/**
 * Gift cards — ledger-only. The café takes the money however it already
 * does; this holds the balance, delivers the code, and shows what is still
 * owed. Staff draw a card down from the /scan surface, not from here.
 */
export function AdminGiftCards() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [filter, setFilter] = useState<StatusFilter>('active')
  const [issueOpen, setIssueOpen] = useState(false)
  const [detailId, setDetailId] = useState<string | null>(null)

  const listQuery = useQuery({
    queryKey: ['marketing', 'gift-cards', filter],
    queryFn: () => apiClient.listGiftCards(filter),
    staleTime: 15_000,
  })
  const cards: GiftCard[] = listQuery.data?.data?.gift_cards ?? []
  const liability = listQuery.data?.data?.outstanding_liability ?? 0
  const liveCount = listQuery.data?.data?.outstanding_count ?? 0

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['marketing', 'gift-cards'] })

  const onError = (title: string) => (err: unknown) =>
    toast({ title, description: describeApiError(err).message, variant: 'destructive' })

  const send = useMutation({
    mutationFn: (id: string) => apiClient.sendGiftCard(id),
    onSuccess: (res) =>
      toast({
        title: res.data?.channel === 'whatsapp_api' ? 'Sent on WhatsApp' : 'Sent by email',
      }),
    onError: onError('Could not send'),
  })

  const voidCard = useMutation({
    mutationFn: (id: string) => apiClient.voidGiftCard(id),
    onSuccess: () => {
      invalidate()
      toast({ title: 'Gift card cancelled' })
    },
    onError: onError('Could not cancel'),
  })

  return (
    <MarketingShell
      title="Gift cards"
      description="Issue a voucher, send the code, and let staff take each visit off the balance at the counter. You collect the money however you already do — this just remembers what's left."
      actions={
        <Button size="sm" onClick={() => setIssueOpen(true)}>
          <Gift className="mr-1.5 h-4 w-4" aria-hidden /> Issue a gift card
        </Button>
      }
    >
      <div className="space-y-5">
        <div className="rounded-lg border border-border/80 bg-background p-4 shadow-sm">
          <p className="text-sm text-muted-foreground">Still to be spent</p>
          <p className="mt-1 text-3xl font-bold tabular-nums" title={pkr(liability)}>
            Rs {formatPKRCompact(liability)}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            across {liveCount} live {liveCount === 1 ? 'card' : 'cards'} — food
            your guests have already paid for. Cancelled and expired cards
            aren't counted: they can't be redeemed, so you don't owe them.
          </p>
        </div>

        <div className="flex flex-wrap gap-1 rounded-lg bg-muted/60 p-1" role="tablist">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              role="tab"
              aria-selected={filter === f.id}
              onClick={() => setFilter(f.id)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                filter === f.id
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {listQuery.isLoading ? (
          <div className="flex h-28 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : cards.length === 0 ? (
          <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
            No gift cards here yet. Issue one when a guest pays for it — you'll
            get a code to hand over or send.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                  <th className="py-2 pr-3 font-medium">Code</th>
                  <th className="py-2 pr-3 font-medium">For</th>
                  <th className="py-2 pr-3 text-right font-medium">Issued</th>
                  <th className="py-2 pr-3 text-right font-medium">Left</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                  <th className="py-2 font-medium">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {cards.map((g) => {
                  const state = cardState(g)
                  return (
                    <tr key={g.id} className="border-b last:border-0">
                      <td className="py-2 pr-3">
                        <button
                          className="font-mono font-medium underline-offset-2 hover:underline"
                          onClick={() => setDetailId(g.id)}
                        >
                          {g.code}
                        </button>
                      </td>
                      <td className="py-2 pr-3">
                        {g.customer_name || (
                          <span className="text-muted-foreground">Not attached</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {pkr(g.initial_amount)}
                      </td>
                      <td className="py-2 pr-3 text-right font-medium tabular-nums">
                        {pkr(g.balance)}
                      </td>
                      <td className={`py-2 pr-3 ${state.className}`}>{state.label}</td>
                      <td className="py-2">
                        <div className="flex justify-end gap-1">
                          {g.customer_id && g.redeemable && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => send.mutate(g.id)}
                              disabled={send.isPending}
                              title="Send the code to this customer"
                            >
                              <Send className="h-3.5 w-3.5" aria-hidden />
                              <span className="sr-only">Send</span>
                            </Button>
                          )}
                          {g.status === 'active' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => voidCard.mutate(g.id)}
                              disabled={voidCard.isPending}
                              title="Cancel this gift card"
                            >
                              <Ban className="h-3.5 w-3.5 text-destructive" aria-hidden />
                              <span className="sr-only">Cancel</span>
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <IssueGiftCardDialog open={issueOpen} onOpenChange={setIssueOpen} onIssued={invalidate} />
      <GiftCardDetailDialog
        id={detailId}
        onOpenChange={() => setDetailId(null)}
        onChanged={invalidate}
      />
    </MarketingShell>
  )
}

/**
 * Search-as-you-type customer picker. Gift cards are frequently bought for
 * someone specific, and without a customer the card can never be sent — so
 * this is on the issue path, not buried in a later edit screen.
 */
function CustomerPicker({
  value,
  onChange,
  autoFocus,
}: {
  value: CustomerRow | null
  onChange: (c: CustomerRow | null) => void
  autoFocus?: boolean
}) {
  const [term, setTerm] = useState('')
  const [debounced, setDebounced] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 250)
    return () => clearTimeout(t)
  }, [term])

  const results = useQuery({
    queryKey: ['gift-card-customer-search', debounced],
    queryFn: () => apiClient.listCustomers({ search: debounced, page_size: 6 }),
    enabled: debounced.length >= 2,
    staleTime: 30_000,
  })
  const items: CustomerRow[] = results.data?.data?.items ?? []

  if (value) {
    return (
      <div className="flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2 text-sm">
        <span>
          <span className="font-medium">{value.display_name || 'Unnamed'}</span>
          {value.phone && <span className="text-muted-foreground"> · {value.phone}</span>}
        </span>
        <Button variant="ghost" size="sm" onClick={() => onChange(null)} title="Remove">
          <X className="h-3.5 w-3.5" aria-hidden />
          <span className="sr-only">Remove customer</span>
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-1">
      <Input
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder="Search by name or phone…"
        autoComplete="off"
        autoFocus={autoFocus}
      />
      {debounced.length >= 2 && (
        <div className="max-h-40 overflow-y-auto rounded-md border">
          {results.isLoading ? (
            <div className="flex h-14 items-center justify-center">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <p className="p-3 text-xs text-muted-foreground">
              Nobody matches “{debounced}”. You can leave this blank and attach
              them later.
            </p>
          ) : (
            <ul>
              {items.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-muted"
                    onClick={() => {
                      onChange(c)
                      setTerm('')
                    }}
                  >
                    <span className="font-medium">{c.display_name || 'Unnamed'}</span>
                    {c.phone && <span className="text-muted-foreground"> · {c.phone}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

function IssueGiftCardDialog({
  open,
  onOpenChange,
  onIssued,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  onIssued: () => void
}) {
  const { toast } = useToast()
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [customer, setCustomer] = useState<CustomerRow | null>(null)
  const [issued, setIssued] = useState<{ code: string; amount: number } | null>(null)

  const parsed = Number(amount)
  const valid = Number.isFinite(parsed) && parsed > 0

  const issue = useMutation({
    mutationFn: () =>
      apiClient.issueGiftCard({
        amount: parsed,
        customer_id: customer?.id,
        note: note.trim() || undefined,
        expires_at: expiresAt || undefined,
      }),
    onSuccess: (res) => {
      // Show the code rather than closing: this is the one moment it's on
      // screen, and staff usually need to write it on a physical card.
      setIssued({ code: res.data?.code ?? '', amount: res.data?.amount ?? parsed })
      setAmount('')
      setNote('')
      setExpiresAt('')
      setCustomer(null)
      onIssued()
    },
    onError: (err: unknown) =>
      toast({
        title: 'Could not issue',
        description: describeApiError(err).message,
        variant: 'destructive',
      }),
  })

  const close = () => {
    setIssued(null)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{issued ? 'Gift card ready' : 'Issue a gift card'}</DialogTitle>
          <DialogDescription>
            {issued
              ? 'Write this code on the card, or send it to the guest from the list.'
              : 'Take the money as you normally would, then record the value here.'}
          </DialogDescription>
        </DialogHeader>

        {issued ? (
          <div className="rounded-lg border bg-muted/40 p-5 text-center">
            <p className="font-mono text-2xl font-bold tracking-wider">{issued.code}</p>
            <p className="mt-1 text-sm text-muted-foreground">worth {pkr(issued.amount)}</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="gc-amount">Value (PKR)</Label>
              <Input
                id="gc-amount"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="5000"
                autoFocus
              />
            </div>
            <div className="space-y-1">
              <Label>Who is it for? (optional)</Label>
              <CustomerPicker value={customer} onChange={setCustomer} />
              <p className="text-xs text-muted-foreground">
                Attach a customer to send them the code and put their
                redemptions on their visit history. You can attach one later
                from the card's details.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="gc-expiry">Expires (optional)</Label>
              <Input
                id="gc-expiry"
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Leave blank for no expiry. A card stays usable all the way
                through the date you pick, then drops out of what you owe.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="gc-note">Note (optional)</Label>
              <Input
                id="gc-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Paid cash · birthday gift"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          {issued ? (
            <Button onClick={close}>Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={close} disabled={issue.isPending}>
                Cancel
              </Button>
              <Button onClick={() => issue.mutate()} disabled={!valid || issue.isPending}>
                {issue.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                Issue
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function GiftCardDetailDialog({
  id,
  onOpenChange,
  onChanged,
}: {
  id: string | null
  onOpenChange: (v: boolean) => void
  onChanged: () => void
}) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [adjusting, setAdjusting] = useState(false)
  const [delta, setDelta] = useState('')
  const [reason, setReason] = useState('')
  const [attaching, setAttaching] = useState(false)
  const [pick, setPick] = useState<CustomerRow | null>(null)

  const detail = useQuery({
    queryKey: ['marketing', 'gift-card', id],
    queryFn: () => apiClient.getGiftCard(id as string),
    enabled: !!id,
  })
  const card = detail.data?.data
  const rows = useMemo(() => card?.transactions ?? [], [card])

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['marketing', 'gift-card', id] })
    onChanged()
  }
  const fail = (title: string) => (err: unknown) =>
    toast({ title, description: describeApiError(err).message, variant: 'destructive' })

  const adjust = useMutation({
    mutationFn: () =>
      apiClient.adjustGiftCard(id as string, { delta: Number(delta), reason: reason.trim() }),
    onSuccess: () => {
      setAdjusting(false)
      setDelta('')
      setReason('')
      refresh()
      toast({ title: 'Balance adjusted' })
    },
    onError: fail('Could not adjust'),
  })

  const attach = useMutation({
    mutationFn: () => apiClient.attachGiftCardCustomer(id as string, pick?.id ?? ''),
    onSuccess: () => {
      setAttaching(false)
      setPick(null)
      refresh()
      toast({ title: 'Gift card updated' })
    },
    onError: fail('Could not attach'),
  })

  const deltaNum = Number(delta)
  const deltaValid = delta.trim() !== '' && Number.isFinite(deltaNum) && deltaNum !== 0

  return (
    <Dialog open={!!id} onOpenChange={(v) => !v && onOpenChange(false)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-mono">{card?.code ?? 'Gift card'}</DialogTitle>
          <DialogDescription>
            {card
              ? `${pkr(card.balance)} left of ${pkr(card.initial_amount)}${
                  card.customer_name ? ` · ${card.customer_name}` : ' · not attached'
                }`
              : 'Loading…'}
          </DialogDescription>
        </DialogHeader>

        {card && card.status === 'active' && (
          <div className="flex flex-wrap gap-2">
            {!adjusting && (
              <Button variant="outline" size="sm" onClick={() => setAdjusting(true)}>
                <Pencil className="mr-1.5 h-3.5 w-3.5" aria-hidden /> Adjust balance
              </Button>
            )}
            {!attaching && (
              <Button variant="outline" size="sm" onClick={() => setAttaching(true)}>
                <UserPlus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                {card.customer_id ? 'Change customer' : 'Attach a customer'}
              </Button>
            )}
          </div>
        )}

        {adjusting && (
          <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
            <p className="text-xs text-muted-foreground">
              Fixing a mis-keyed redemption, or adding value. This writes a new
              line in the history — the original stays visible, because the
              ledger can't be edited.
            </p>
            <div className="space-y-1">
              <Label htmlFor="gc-delta">Amount (negative to take value off)</Label>
              <Input
                id="gc-delta"
                inputMode="decimal"
                value={delta}
                onChange={(e) => setDelta(e.target.value)}
                placeholder="1100"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="gc-reason">Reason</Label>
              <Input
                id="gc-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Staff keyed 1200 instead of 100"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setAdjusting(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => adjust.mutate()}
                disabled={!deltaValid || !reason.trim() || adjust.isPending}
              >
                {adjust.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                Save adjustment
              </Button>
            </div>
          </div>
        )}

        {attaching && (
          <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
            <Label>Attach to</Label>
            <CustomerPicker value={pick} onChange={setPick} autoFocus />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setAttaching(false)}>
                Cancel
              </Button>
              <Button size="sm" onClick={() => attach.mutate()} disabled={attach.isPending}>
                {attach.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                {pick ? 'Attach' : 'Detach'}
              </Button>
            </div>
          </div>
        )}

        {detail.isLoading ? (
          <div className="flex h-24 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <ul className="max-h-60 space-y-1.5 overflow-y-auto">
            {rows.map((t) => (
              <li key={t.id} className="flex items-baseline gap-3 rounded-md border p-2 text-sm">
                <span
                  className={`w-24 shrink-0 text-right font-medium tabular-nums ${
                    t.delta < 0 ? 'text-destructive' : 'text-emerald-600'
                  }`}
                >
                  {t.delta > 0 ? '+' : ''}
                  {pkr(t.delta)}
                </span>
                <span className="flex-1">
                  {t.note || (t.delta < 0 ? 'Redeemed' : 'Added')}
                  {t.created_by_name && (
                    <span className="text-muted-foreground"> · {t.created_by_name}</span>
                  )}
                </span>
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                  {pkr(t.balance_after)} left
                </span>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-muted-foreground">
          This history can't be edited or deleted — it's the record of money
          your guests have already handed over.
        </p>
      </DialogContent>
    </Dialog>
  )
}
